import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import useOrgId from "../../../hooks/useOrgId";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { fetchNotificationsAsync } from "../../../store/thunks/NotificationsThunk";
import { resetNotifications, NotificationItem } from "../../../store/slices/NotificationsSlice";
import { canViewNotifications, NOTIFICATION_STATUS_OPTIONS } from "../../../constants/notifications";
import { Field, Label } from "../../Catalyst/fieldset";
import { Select } from "../../Catalyst/select";
import { Input, InputGroup } from "../../Catalyst/input";
import {
  Pagination,
  PaginationPrevious,
  PaginationList,
  PaginationPage,
  PaginationNext,
} from "../../Catalyst/pagination";
import NotificationsTable from "./NotificationsTable";
import NotificationDetailsDialog from "./NotificationDetailsDialog";

const PER_PAGE = 20;

const LoadingState = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="h-8 w-8 animate-spin text-zinc-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  </div>
);

const Notifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useOrgId();
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);
  const { notifications, status, error, pages, totalItems } = useSelector(
    (state: RootState) => state.NotificationsStore
  );

  // Access control: only specific admin roles may view notifications.
  const canView = canViewNotifications(userAccount?.user_role);

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  // typeInput drives the controlled field; typeFilter is the debounced value that triggers fetches.
  const [typeInput, setTypeInput] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Reset to the first page whenever the active organization changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [orgId]);

  // Fetch notifications whenever org, page, or filters change.
  // Gated on canView so we never fire a request the user isn't authorized for.
  useEffect(() => {
    if (!orgId || !canView) {
      dispatch(resetNotifications());
      return;
    }
    dispatch(
      fetchNotificationsAsync({
        orgId,
        page: currentPage,
        perPage: PER_PAGE,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      })
    );
  }, [orgId, canView, currentPage, statusFilter, typeFilter, dispatch]);

  // Clear results when navigating away from the page.
  useEffect(() => {
    return () => {
      dispatch(resetNotifications());
    };
  }, [dispatch]);

  // The type filter matches ruleKey or insightType server-side; debounce typing.
  const debouncedSetType = useMemo(
    () =>
      debounce((value: string) => {
        setTypeFilter(value.trim());
        setCurrentPage(1);
      }, 300),
    []
  );
  useEffect(() => () => debouncedSetType.cancel(), [debouncedSetType]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypeInput(e.target.value);
    debouncedSetType(e.target.value);
  };

  const handleViewDetails = useCallback((notification: NotificationItem) => {
    setSelectedNotification(notification);
    setIsDetailsOpen(true);
  }, []);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pages) return;
    setCurrentPage(page);
  };

  // Access control: only specific admin roles may view this screen.
  // userAccount is fetched in AppLayout; treat its absence as "still loading".
  if (!userAccount) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <div className="desktext14 text-texthead">Access restricted</div>
          <p className="max-w-md text-sm text-textColor">
            You do not have permission to view notifications. Please contact an administrator if you
            believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  const isLoading = status === STATUS.LOADING || status === STATUS.IDLE;
  const isFailed = status === STATUS.FAILED;
  const isEmpty = status === STATUS.SUCCESS && notifications.length === 0;
  const hasResults = status === STATUS.SUCCESS && notifications.length > 0;

  const renderBody = () => {
    if (!orgId) {
      return (
        <div className="flex h-[400px] items-center justify-center px-6">
          <p className="max-w-md text-center text-sm text-[#667085]">
            No organization selected. Select an organization from the Organizations tab to view its
            notifications.
          </p>
        </div>
      );
    }

    if (isLoading) {
      return <LoadingState />;
    }

    if (isFailed) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
          <p className="max-w-md text-center text-sm font-medium text-red-500">
            {error || "Something went wrong while loading notifications. Please try again."}
          </p>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-20">
          <p className="text-sm font-medium text-texthead opacity-70">No notifications found</p>
          <p className="max-w-md text-center text-sm text-textColor">
            There are no notifications matching your filters for this organization.
          </p>
        </div>
      );
    }

    return (
      <div className="px-6">
        <NotificationsTable notifications={notifications} onViewDetails={handleViewDetails} />
      </div>
    );
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="desktext10">Notifications</div>
          <div className="mt-1 font-inter text-sm font-normal leading-5 text-textColor">
            Review notifications sent to users in this organization
          </div>
        </div>
      </div>

      <div className="mt-5 w-full rounded-lg border-2 border-shadowborder pt-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end justify-between gap-4 px-6 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <Field className="w-[200px]">
              <Label>Status</Label>
              <Select value={statusFilter} onChange={handleStatusChange} aria-label="Filter by status">
                <option value="">All statuses</option>
                {NOTIFICATION_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field className="w-[320px]">
              <Label>Type</Label>
              <InputGroup>
                <MagnifyingGlassIcon />
                <Input
                  name="type"
                  value={typeInput}
                  onChange={handleTypeChange}
                  placeholder="Filter by type or rule key"
                  aria-label="Filter by type"
                />
              </InputGroup>
            </Field>
          </div>

          {hasResults && (
            <div className="pb-2 text-sm text-textColor">
              {totalItems} {totalItems === 1 ? "notification" : "notifications"}
            </div>
          )}
        </div>

        {/* Body: states + table */}
        {renderBody()}

        {/* Pagination */}
        {hasResults && pages > 1 && (
          <div className="px-6 py-4">
            <Pagination>
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <PaginationList>
                {(() => {
                  const items: React.ReactNode[] = [];
                  const addPage = (num: number) => {
                    items.push(
                      <PaginationPage
                        key={num}
                        onClick={() => handlePageChange(num)}
                        current={num === currentPage}
                      >
                        {String(num)}
                      </PaginationPage>
                    );
                  };
                  const addEllipsis = (key: string) => {
                    items.push(
                      <span key={key} className="px-1 text-sm text-zinc-500">
                        ...
                      </span>
                    );
                  };

                  if (pages <= 5) {
                    for (let i = 1; i <= pages; i++) addPage(i);
                  } else {
                    addPage(1);
                    if (currentPage > 3) addEllipsis("left");
                    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
                      if (p > 1 && p < pages) addPage(p);
                    }
                    if (currentPage < pages - 2) addEllipsis("right");
                    addPage(pages);
                  }
                  return items;
                })()}
              </PaginationList>
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pages}
              />
            </Pagination>
          </div>
        )}
      </div>

      <NotificationDetailsDialog
        notification={selectedNotification}
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
      />
    </div>
  );
};

export default Notifications;
